import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    Avatar,
    CircularProgress,
    Container,
    Stack,
    Chip,
    Divider,
    Paper,
    IconButton,
    Tooltip,
    Alert,
    Fade,
    Skeleton
} from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    SportsSoccer as SportsSoccerIcon,
    EmojiEvents as EmojiEventsIcon,
    People as PeopleIcon,
    TrendingUp as TrendingUpIcon,
    Star as StarIcon,
    WorkspacePremium as WorkspacePremiumIcon
} from "@mui/icons-material";
import { contestServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { IMAGE_BASE } from "../../utils/constants";
import { capitalizeFirstLetter } from "../../utils/helpers";
import CardLayout from "../../layouts/CardLayout";

// Loading skeleton component
const TeamPreviewSkeleton = () => (
    <Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 3, borderRadius: 2 }} />
        <Grid container spacing={3}>
            {[1, 2, 3, 4, 5, 6].map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item}>
                    <Card sx={{ p: 2 }}>
                        <Skeleton variant="circular" width={60} height={60} sx={{ mx: 'auto', mb: 2 }} />
                        <Skeleton variant="text" width="80%" sx={{ mx: 'auto' }} />
                        <Skeleton variant="text" width="60%" sx={{ mx: 'auto' }} />
                    </Card>
                </Grid>
            ))}
        </Grid>
    </Box>
);

// Player card component
const PlayerCard = ({ player, position }) => {
    const getPositionColor = (pos) => {
        switch (parseInt(pos)) {
            case 24: // goalkeeper
                return '#FF6B6B';
            case 25: // defender
                return '#4ECDC4';
            case 26: // midfielder
                return '#45B7D1';
            case 27: // forward
                return '#96CEB4';
            default:
                return '#95A5A6';
        }
    };

    const getPositionIcon = (pos) => {
        switch (parseInt(pos)) {
            case 24: // goalkeeper
                return '🥅';
            case 25: // defender
                return '🛡️';
            case 26: // midfielder
                return '⚽';
            case 27: // forward
                return '🎯';
            default:
                return '👤';
        }
    };

    const getPositionName = (pos) => {
        switch (parseInt(pos)) {
            case 24:
                return 'Goalkeeper';
            case 25:
                return 'Defender';
            case 26:
                return 'Midfielder';
            case 27:
                return 'Forward';
            default:
                return 'Player';
        }
    };

    return (
        <Fade in timeout={500}>
            <Card 
                sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    background: `linear-gradient(135deg, ${getPositionColor(position)}10 0%, rgba(0,0,0,0.02) 100%)`,
                    border: `2px solid ${getPositionColor(position)}30`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 8px 25px ${getPositionColor(position)}40`,
                        transition: 'all 0.3s ease'
                    }
                }}
            >
                <Box sx={{ position: 'relative', mb: 2 }}>
                    <Avatar
                        src={player.imagePath || `${IMAGE_BASE}${player.image || player.photo}`}
                        alt={player.displayName || player.name}
                        sx={{ 
                            width: 80, 
                            height: 80, 
                            mx: 'auto',
                            border: `3px solid ${getPositionColor(position)}`,
                            boxShadow: `0 0 20px ${getPositionColor(position)}40`
                        }}
                    />
                    <Chip
                        icon={<span>{getPositionIcon(position)}</span>}
                        label={getPositionName(position)}
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: -10,
                            right: -10,
                            bgcolor: getPositionColor(position),
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '0.7rem'
                        }}
                    />
                </Box>
                
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: 'text.primary' }}>
                    {player.displayName || player.fullName || player.name || 'Unknown Player'}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {player.fullName && player.displayName !== player.fullName ? player.fullName : 'Player'}
                </Typography>

                {/* Captain/Vice Captain Badges */}
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 2 }}>
                    {player.captain && (
                        <Chip
                            label="C"
                            size="small"
                            sx={{
                                bgcolor: '#FFD700',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                height: 20
                            }}
                        />
                    )}
                    {player.viceCaptain && (
                        <Chip
                            label="VC"
                            size="small"
                            sx={{
                                bgcolor: '#C0C0C0',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                height: 20
                            }}
                        />
                    )}
                    {player.substitutePlayer && (
                        <Chip
                            label="SUB"
                            size="small"
                            sx={{
                                bgcolor: '#95A5A6',
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                                height: 20
                            }}
                        />
                    )}
                </Stack>

                <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 'auto' }}>
                    <Box textAlign="center">
                        <Typography variant="caption" color="text.secondary">
                            Points
                        </Typography>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                            {player.scoredPoints || 0}
                        </Typography>
                    </Box>
                    <Box textAlign="center">
                        <Typography variant="caption" color="text.secondary">
                            Price
                        </Typography>
                        <Typography variant="h6" color="secondary" sx={{ fontWeight: 'bold' }}>
                            ${player.price || 0}
                        </Typography>
                    </Box>
                </Stack>
            </Card>
        </Fade>
    );
};

const TeamPreviewScreen = () => {
    const { id } = useParams(); // Team ID from URL
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [teamData, setTeamData] = useState(null);
    const [players, setPlayers] = useState([]);
    const [teamInfo, setTeamInfo] = useState(null);

    useEffect(() => {
        if (id) {
            fetchTeamData();
        }
    }, [id]);

    const fetchTeamData = async () => {
        try {
            setLoading(true);
            const response = await contestServices.getFantasyGameTeamById({ id });
            
            if (response?.data?.data) {
                const data = response.data.data;
                setTeamData(data);
                setPlayers(data.players || []);
                
                // Calculate dynamic formation based on starting players
                const startingPlayers = data.players?.filter(p => !p.substitutePlayer) || [];
                const formation = calculateFormation(startingPlayers);
                
                // Calculate total value by summing all player prices
                const totalValue = data.players?.reduce((sum, player) => {
                    const playerPrice = parseFloat(player.price) || 0;
                    return sum + playerPrice;
                }, 0) || 0;

                setTeamInfo({
                    teamName: data.name || 'Fantasy Team',
                    totalPoints: data.totalPoints || 0,
                    totalValue: totalValue,
                    formation: formation,
                    captain: data.players?.find(p => p.captain)?.playerId,
                    viceCaptain: data.players?.find(p => p.viceCaptain)?.playerId,
                    remainBudget: data.remainBudget || 0,
                    contestInfo: {
                        contestName: data.contestId?.contestName,
                        leagueName: data.leagueId?.name,
                        contestType: data.contestId?.contestType?.type || data.gameType,
                        gameType: data.gameType,
                        entryFee: data.contestId?.entryFee,
                        contestStatus: data.contestId?.contestStatus,
                        roomSize: data.contestId?.roomSize?.type === 'limited' 
                            ? `${data.contestId?.roomSize?.joinedRoomSize}/${data.contestId?.roomSize?.totalRoomSize}`
                            : data.contestId?.roomSize?.type || 'Unlimited',
                        gameDuration: data.contestId?.gameDuration?.type || 'Unknown'
                    }
                });
            }
        } catch (error) {
            errorHandler(error);
        } finally {
            setLoading(false);
        }
    };

    const calculateFormation = (players) => {
        const positions = {
            goalkeeper: 0,
            defender: 0,
            midfielder: 0,
            forward: 0
        };

        players.forEach(player => {
            const position = parseInt(player.position || player.role || player.playerPosition || 26);
            switch (position) {
                case 24: // goalkeeper
                    positions.goalkeeper++;
                    break;
                case 25: // defender
                    positions.defender++;
                    break;
                case 26: // midfielder
                    positions.midfielder++;
                    break;
                case 27: // forward
                    positions.forward++;
                    break;
                default:
                    positions.midfielder++; // Default fallback
            }
        });

        return `${positions.goalkeeper}-${positions.defender}-${positions.midfielder}-${positions.forward}`;
    };

    const getFormationLayout = (formation) => {
        // Default formation layouts
        const formations = {
            '4-4-2': { goalkeeper: 1, defender: 4, midfielder: 4, forward: 2 },
            '4-3-3': { goalkeeper: 1, defender: 4, midfielder: 3, forward: 3 },
            '3-5-2': { goalkeeper: 1, defender: 3, midfielder: 5, forward: 2 },
            '4-5-1': { goalkeeper: 1, defender: 4, midfielder: 5, forward: 1 },
            '3-4-3': { goalkeeper: 1, defender: 3, midfielder: 4, forward: 3 }
        };
        return formations[formation] || formations['4-4-2'];
    };

    const groupPlayersByPosition = (players, startingOnly = false) => {
        const grouped = {
            goalkeeper: [],
            defender: [],
            midfielder: [],
            forward: []
        };

        const playersToGroup = startingOnly ? players.filter(p => !p.substitutePlayer) : players;

        playersToGroup.forEach(player => {
            const position = parseInt(player.position || player.role || player.playerPosition || 26);
            switch (position) {
                case 24: // goalkeeper
                    grouped.goalkeeper.push(player);
                    break;
                case 25: // defender
                    grouped.defender.push(player);
                    break;
                case 26: // midfielder
                    grouped.midfielder.push(player);
                    break;
                case 27: // forward
                    grouped.forward.push(player);
                    break;
                default:
                    grouped.midfielder.push(player); // Default fallback
            }
        });

        return grouped;
    };

    const groupedPlayers = groupPlayersByPosition(players);
    const startingPlayers = groupPlayersByPosition(players, true);
    const substitutes = players.filter(p => p.substitutePlayer);
    const formation = getFormationLayout(teamInfo?.formation);

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <CardLayout>
                {/* Header Section */}
                <Box sx={{ mb: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                        <IconButton 
                            onClick={() => navigate(-1)}
                            sx={{ 
                                bgcolor: 'primary.main', 
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.dark' }
                            }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                Team Preview
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {teamInfo?.teamName || 'Fantasy Team'}
                            </Typography>
                        </Box>
                    </Stack>

                    {/* Team Stats */}
                    {teamInfo && (
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12} sm={6} md={4}>
                                <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
                                    <TrendingUpIcon sx={{ fontSize: 30, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        {teamInfo.totalPoints}
                                    </Typography>
                                    <Typography variant="body2">
                                        Total Points
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'secondary.main', color: 'white' }}>
                                    <WorkspacePremiumIcon sx={{ fontSize: 30, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        ${teamInfo.totalValue}
                                    </Typography>
                                    <Typography variant="body2">
                                        Team Value
                                    </Typography>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <Card sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.main', color: 'white' }}>
                                    <PeopleIcon sx={{ fontSize: 30, mb: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        {players.length}
                                    </Typography>
                                    <Typography variant="body2">
                                        Players
                                    </Typography>
                                </Card>
                            </Grid>
                        </Grid>
                    )}

                    {/* Contest Information */}
                    {teamInfo?.contestInfo && (
                        <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                Contest Information
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Contest Name
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {teamInfo.contestInfo.contestName}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        League
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {teamInfo.contestInfo.leagueName}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Contest Type
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {capitalizeFirstLetter(teamInfo.contestInfo.contestType)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Game Type
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {capitalizeFirstLetter(teamInfo.contestInfo.gameType)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Entry Fee
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        ${teamInfo.contestInfo.entryFee}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Contest Status
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {capitalizeFirstLetter(teamInfo.contestInfo.contestStatus)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Room Size
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {teamInfo.contestInfo.roomSize}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">
                                        Game Duration
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                        {capitalizeFirstLetter(teamInfo.contestInfo.gameDuration)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Paper>
                    )}
                </Box>

                {loading ? (
                    <TeamPreviewSkeleton />
                ) : players.length === 0 ? (
                    <Fade in>
                        <Card sx={{ p: 6, textAlign: 'center' }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '300px',
                                    opacity: 0.7
                                }}
                            >
                                <SportsSoccerIcon sx={{ fontSize: 80, mb: 2, color: 'text.disabled' }} />
                                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    No Team Data Found
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    This team doesn't have any players or the team data is not available.
                                </Typography>
                                <Alert severity="info" sx={{ maxWidth: 400 }}>
                                    Please check if the team ID is correct or try again later.
                                </Alert>
                            </Box>
                        </Card>
                    </Fade>
                ) : (
                    <Fade in>
                        <Box>
                            {/* <Paper 
                                elevation={0} 
                                sx={{ 
                                    p: 4, 
                                    mb: 4, 
                                    background: 'linear-gradient(135deg, rgba(0, 161, 221, 0.02) 0%, rgba(217, 83, 35, 0.02) 100%)',
                                    border: '1px solid rgba(0, 161, 221, 0.1)',
                                    borderRadius: 2
                                }}
                            > */}
                                {/* <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center' }}>
                                    ⚽ Team Formation: {teamInfo?.formation || 'Calculating...'}
                                </Typography>
                                
                                <Box sx={{ 
                                    display: 'flex', 
                                    flexDirection: 'column', 
                                    alignItems: 'center',
                                    gap: 2,
                                    minHeight: 400,
                                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                                    borderRadius: 2,
                                    p: 3,
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}> */}
                                    {/* Field lines */}
                                    {/* <Box sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: `
                                            linear-gradient(90deg, transparent 0%, transparent 48%, #ddd 49%, #ddd 51%, transparent 52%),
                                            linear-gradient(0deg, transparent 0%, transparent 48%, #ddd 49%, #ddd 51%, transparent 52%)
                                        `,
                                        opacity: 0.3
                                    }} />
                                    
                                    {startingPlayers.goalkeeper.map((player, index) => (
                                        <Box key={index} sx={{ position: 'absolute', bottom: 20 }}>
                                            <PlayerCard player={player} position={24} />
                                        </Box>
                                    ))}
                                    
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 2, 
                                        position: 'absolute', 
                                        bottom: 120,
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        width: '100%'
                                    }}>
                                        {startingPlayers.defender.map((player, index) => (
                                            <Box key={index} sx={{ minWidth: 200 }}>
                                                <PlayerCard player={player} position={25} />
                                            </Box>
                                        ))}
                                    </Box>
                                    
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 2, 
                                        position: 'absolute', 
                                        bottom: 240,
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        width: '100%'
                                    }}>
                                        {startingPlayers.midfielder.map((player, index) => (
                                            <Box key={index} sx={{ minWidth: 200 }}>
                                                <PlayerCard player={player} position={26} />
                                            </Box>
                                        ))}
                                    </Box>
                                    
                                    <Box sx={{ 
                                        display: 'flex', 
                                        gap: 2, 
                                        position: 'absolute', 
                                        bottom: 360,
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                        width: '100%'
                                    }}>
                                        {startingPlayers.forward.map((player, index) => (
                                            <Box key={index} sx={{ minWidth: 200 }}>
                                                <PlayerCard player={player} position={27} />
                                            </Box>
                                        ))}
                                    </Box>
                                </Box> */}
                            {/* </Paper> */}

                            {/* Starting Players */}
                            <Paper elevation={0} sx={{ p: 4, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                                    ⚽ Starting XI
                                </Typography>
                                
                                <Grid container spacing={3}>
                                    {players.filter(p => !p.substitutePlayer).map((player, index) => (
                                        <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                            <PlayerCard 
                                                player={player} 
                                                position={parseInt(player.position || player.role || player.playerPosition || 26)} 
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Paper>

                            {/* Substitutes */}
                            {substitutes.length > 0 && (
                                <Paper elevation={0} sx={{ p: 4, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                    <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                                        🔄 Substitutes
                                    </Typography>
                                    
                                    <Grid container spacing={3}>
                                        {substitutes.map((player, index) => (
                                            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                                                <PlayerCard 
                                                    player={player} 
                                                    position={parseInt(player.position || player.role || player.playerPosition || 26)} 
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Paper>
                            )}
                        </Box>
                    </Fade>
                )}
            </CardLayout>
        </Container>
    );
};

export default TeamPreviewScreen;